import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import styled from "@emotion/styled";
import { RemoveRedEye as RemoveRedEyeIcon } from "@mui/icons-material";
import {
  IconButton,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { spacing } from "@mui/system";
import { NavLink } from "react-router-dom";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Paper = styled(MuiPaper)`
  ${spacing}
  background-color: ${(props) => props.theme.palette.background.paper};
  color: ${(props) => props.theme.palette.text.primary};
`;

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)});
  background-color: ${(props) => props.theme.palette.background.paper};
`;

interface BillDataItem {
  programName: string;
  amount: number;
  taxable: boolean;
  status: string;
  requestingApplication: string;
  requestingClient: string;
  invoiceDate: string;
  paymentService: {
    billRefCode: string;
    paymentStatus: string;
    paymentRefCode: string;
  };
}

interface PayeeBillsProps {
  billData: BillDataItem[];
}

export default function PayeeBills(props: PayeeBillsProps) {
  const { billData } = props;
  const [value, setValue] = React.useState(0);
  const theme = useTheme();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          sx={{
            "& .MuiTab-root": {
              color: theme.palette.text.primary,
            },
            "& .Mui-selected": {
              color: theme.palette.primary.main,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <Tab label="Bills" {...a11yProps(0)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <Paper>
          <TableWrapper>
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.divider,
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: theme.palette.background.default,
                  fontWeight: "bold",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Application</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Invoice Date</TableCell>
                  <TableCell>Bill RefCode</TableCell>
                  <TableCell>Bill Status</TableCell>
                  <TableCell>Bill Amount</TableCell>
                  <TableCell>Taxable</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor:
                        index % 2 === 0
                          ? theme.palette.background.paper
                          : theme.palette.background.default,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row.requestingApplication}
                    </TableCell>
                    <TableCell>{row.requestingClient}</TableCell>
                    <TableCell>{row.programName}</TableCell>
                    <TableCell>
                      {new Intl.DateTimeFormat("en-US", {
                        dateStyle: "medium",
                      }).format(new Date(row.invoiceDate))}
                    </TableCell>
                    <TableCell>
                      {row.paymentService?.billRefCode || "N/A"}
                    </TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>
                      {row.amount.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </TableCell>
                    <TableCell>{row.taxable ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      {row.paymentService?.paymentStatus || "N/A"}
                    </TableCell>
                    <TableCell padding="none" align="left">
                      <Box mr={2}>
                        <IconButton
                          aria-label="details"
                          component={NavLink}
                          to={`/bills/detail/${
                            row.paymentService?.billRefCode || "N/A"
                          }`}
                          size="large"
                          disabled={!row.paymentService?.billRefCode}
                          sx={{
                            color: theme.palette.text.primary,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                            "&.Mui-disabled": {
                              color: theme.palette.text.disabled,
                            },
                          }}
                        >
                          <RemoveRedEyeIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        </Paper>
      </CustomTabPanel>
    </Box>
  );
}
